/**
 * Dummy plan catalog — STOPGAP until the BFF plan API ships (later today).
 *
 * The shape here mirrors what we expect the BFF to return so the swap is a
 * drop-in: only `src/api/plans.js` changes, not the UI. Three tiers —
 * one free, two paid.
 *
 * Current plan lives on the signed-in user object as `user.planId` (persisted
 * via UserContext.updateUser for now). When the BFF lands, the tenant's plan
 * comes down with the session/tenant payload and populates the same field.
 */

export const DEFAULT_PLAN_ID = "free";

export const PLANS = [
  {
    id: "free",
    name: "Free",
    tier: 0,
    priceMonthly: 0,
    tagline: "Get your PBX online.",
    highlight: false,
    features: [
      "Up to 5 extensions",
      "1 ring group",
      "Voicemail to email",
      "Community support",
    ],
    limits: { extensions: 5, ringGroups: 1, ivr: 0, recording: false },
  },
  {
    id: "pro",
    name: "Pro",
    tier: 1,
    priceMonthly: 49,
    tagline: "Everything a growing team needs.",
    highlight: true, // recommended tier
    features: [
      "Unlimited extensions",
      "Unlimited ring groups",
      "IVR auto-attendant",
      "Call recording",
      "AI setup assistant",
      "Email support",
    ],
    limits: { extensions: null, ringGroups: null, ivr: 10, recording: true },
  },
  {
    id: "business",
    name: "Business",
    tier: 2,
    priceMonthly: 149,
    tagline: "Scale, compliance & priority support.",
    highlight: false,
    features: [
      "Everything in Pro",
      "Multi-extension cabins",
      "Calling cards & rate plans",
      "Retention & compliance policies",
      "Priority 24/7 support",
      "99.99% uptime SLA",
    ],
    limits: { extensions: null, ringGroups: null, ivr: null, recording: true },
  },
];

/** Look up a plan by id, falling back to the free default. */
export function getPlan(planId) {
  return (
    PLANS.find((p) => p.id === planId) ||
    PLANS.find((p) => p.id === DEFAULT_PLAN_ID)
  );
}

/** The current plan id for a user object (defaults to free when unset). */
export function currentPlanId(user) {
  return user?.planId || DEFAULT_PLAN_ID;
}

/** Is the given plan a paid tier? */
export function isPaid(plan) {
  return !!plan && plan.tier > 0;
}

/** The cheapest paid plan above the given tier — used for the "upgrade" nudge. */
export function nextPaidPlan(fromTier = 0) {
  return PLANS.filter((p) => p.tier > fromTier).sort(
    (a, b) => a.tier - b.tier,
  )[0];
}
